import React, { PropsWithChildren } from 'react';
import {
    MappedComponentProperties,
} from '@adobe/aem-react-editable-components';

import './FaqMenu.css';
import useViewState from './hooks/useViewState';
import withAsyncImport from '../../withAsyncImport';
import { Container } from '../../importCL';

const FaqMenuContactFooter = withAsyncImport(() => import('./components/ContactFooter/FaqMenuContactFooter'));
const FaqMenuNavDesktop = withAsyncImport(() => import('./components/FaqMenuNavDesktop/FaqMenuNavDesktop'));
const FaqMenuNavMobile = withAsyncImport(() => import('./components/FaqMenuNavMobile/FaqMenuNavMobile'));
const TilesOrLinks = withAsyncImport(() => import('./components/TilesOrLinks/TilesOrLinks'));

interface IIcon {
    iconLabel: string;
    iconPath: string;
    iconAltText: string;
    uuid: string;
}
export interface IQuestion {
      icon: null | IIcon;
      /**
       * In Author Dialogue:
       * for frequentQuestions: Top Questions > FAQ Top Questions > Top Question Relative Link Path
       * will always have a '.html' appended
       */
      url: string | '.html';
      question: string;
      answer: string;
      subcategory: null | string;
      slugOverride: null | string;
    subheading: null | string;
}

export interface ICategory {
    categoryName: string;
    urlAppend: string;
    questionContent: null | IQuestion[];
}

export interface IContactInformation {
    icon?: IIcon;
    firstTitle?: string;
    firstDescription?: string;
    secondTitle?: string;
    secondDescription?: string;
}

interface IFaqMenuProps extends MappedComponentProperties {
    categories: Array<ICategory>;
    categoryPath: string;
    frequentQuestions: Array<IQuestion>;
    mobileHeading: string;
    headline: string;
    mobileHeadline: string;
    questionPage: string;
    contactBackgroundImage?: string;
    contactTitle?: string;
    contactInformation?: Array<IContactInformation>;
    enableCTA?: boolean;
    ctaText?: String;
    ctaLink?: String;
}

export const FaqMenuEditConfig = {
    emptyLabel: 'FAQ Menu',
    isEmpty: (props: PropsWithChildren<IFaqMenuProps>) => {
        return !props || props.categories.length === 0;
    },
};

/**
 * NOTE: In Author, the name of the Page and the name of the FAQ Content Fragment have to match. It is *CASE SENSITIVE*
 */
const FaqMenu = (props: PropsWithChildren<IFaqMenuProps>) => {
    const url =
        'server' in window && window.server && '_path' in window
            ? (window._path as string)
            : window.location.href;
    const urlWithoutQueryParams = url.split('?')[0].replace('.html', '');
    const urlEnd = url.split('?')[0].slice(-5).includes('.html') ? '.html' : '';
    const { viewState, targetIndex } = useViewState(
        urlWithoutQueryParams,
    );

    const currentCategory = props.categories.find(category => {
        return urlWithoutQueryParams
            .split('/')[targetIndex].includes(category.urlAppend.replace('.html', ''));
    });

    if (!props.categories || !currentCategory) return null;

    return (
        <div className='FaqMenu'>
        <Container id='faq-menu' className='flex flex-row flex-nowrap md:flex-col md:p-0 md:gap-0'>
            {props.categories !== undefined && (
                <>
                    <FaqMenuNavDesktop
                        categories={props.categories}
                        currentCategory={currentCategory}
                        urlWithoutQueryParams={urlWithoutQueryParams}
                        urlEnd={urlEnd}
                    />
                    <FaqMenuNavMobile
                        categories={props.categories}
                        currentCategory={currentCategory}
                        mobileHeading={props.mobileHeading}
                        urlWithoutQueryParams={urlWithoutQueryParams}
                        urlEnd={urlEnd}
                    />
                </>
            )}
            <TilesOrLinks
                currentCategory={currentCategory}
                categories={props.categories}
                urlWithoutQueryParams={urlWithoutQueryParams}
                headline={props.headline}
                mobileHeadline={props.mobileHeadline}
                frequentQuestions={props.frequentQuestions}
                viewState={viewState}
                enableCTA={props.enableCTA}
                ctaText={props.ctaText}
                ctaLink={props.ctaLink}
            />
        </Container>
        {props?.contactTitle && 
        <>
            <FaqMenuContactFooter
                backgroundImage={props?.contactBackgroundImage}
                title={props?.contactTitle}
                contactInformation={props?.contactInformation}
            />
        </>
        }
        </div>
    );
};

export default FaqMenu;
